import * as Tooltip from "@radix-ui/react-tooltip";
import clsx from "clsx";
import { getIn, type FormikErrors } from "formik";
import { ArrowLeft, Bell, ChevronRight, Clock, MessageSquareText, User, Route } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatPublicMessage, splitApiRequestFormErrors } from "../../api";
import { type ChatEntry } from "../../chat/chatStore";
import { stop_alert_run, update_alert, type AlertRunProgress } from "../../watchlist/api";
import { useRunProgress } from "../../watchlist/hooks";
import type { Alert, AlertRun, Schedule, WatchlistScope } from "../../watchlist/types";
import { hasProcessSelection } from "../../watchlist/types";
import { SCHEDULE_PRESETS, WEEKLY_DAYS, WINDOW_PRESETS } from "../../watchlist/types";
import type { FilterOption } from "../FilterMultiSelect";
import FilterMultiSelect from "../FilterMultiSelect";
import { ChatMarkdown } from "../chat/ChatMarkdown";
import FollowUpAction from "../chat/FollowUpAction";
import { useAskSidebar } from "../../chat/AskContext";
import { alertResultContext } from "../../chat/alertResultContext";
import { chatThinkingTranslationKey } from "../chat/thinking";
import { DetachedStreamingIndicator, useChatAutoScroll } from "../chat/useChatAutoScroll";

import DaysSelect from "./DaysSelect";
import ScopeChips from "./ScopeChips";
import ScopeSummary from "./ScopeSummary";
import type { AlertHandlers } from "./alertActions";
import { useAlertActions } from "./alertActions";
import { useAlertLabels } from "./labels";

type AlertDetailProps = {
  alert: Alert;
  onBack: () => void;
  handlers: AlertHandlers;
  /** For turning the scope's ids into the names the chat endpoint filters by. */
  userOptions: FilterOption[];
  processOptions: FilterOption[];
  /** Opened by the Edit action rather than by reading the alert. */
  startInEdit?: boolean;
};

type AlertEditorFormValues = {
  title: string;
  prompt: string;
  scope: {
    window: {
      days: number;
    };
  };
  schedule: {
    everyDays: number;
  };
};

/**
 * The answer at a glance for a run whose headline predates the three-part
 * output: the report's own first line, by the rule the backend falls back to.
 */
const firstLine = (response: string): string =>
  (response.split("\n").find(line => line.trim() !== "") ?? "")
    .trim()
    .replace(/^#+\s*/, "")
    .slice(0, 120);

/**
 * A run collapsed to the two things worth scanning a history by: when it ran
 * and what it concluded. The latest one starts open; earlier ones start shut.
 */
const RunRow = ({
  run,
  meta,
  defaultOpen = false,
  onAsk,
}: {
  run: AlertRun;
  meta: string;
  defaultOpen?: boolean;
  onAsk: (run: Extract<AlertRun, { status: "success" }>) => void;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const { t } = useTranslation();
  const failed = run.status === "failed";
  const failure = failed ? formatPublicMessage(run.error, t) : null;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition hover:bg-muted-surface"
      >
        <ChevronRight
          size={14}
          className={clsx("shrink-0 text-text-dim transition-transform", open && "rotate-90")}
        />
        <span className="shrink-0 text-xs text-text-dim">{meta}</span>
        <span className={clsx("truncate text-sm", failed ? "text-danger-strong" : "text-text")}>
          {failed ? t("watchlist.run_failed") : run.headline || firstLine(run.response)}
        </span>
      </button>
      {open && (
        <div
          className={clsx(
            "border-t border-outline px-4 py-3 text-sm",
            failed ? "text-danger-strong" : "text-text",
          )}
        >
          {failed ? (
            failure
          ) : (
            <>
              <ChatMarkdown content={run.response} />
              <FollowUpAction
                icon={MessageSquareText}
                label={t("chat_ask_result")}
                hint={t("chat_ask_result_hint")}
                className="mt-4"
                onClick={() => onAsk(run)}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

const PastRunList = ({
  runs,
  meta,
  onAsk,
}: {
  runs: AlertRun[];
  meta: (run: AlertRun) => string;
  onAsk: (run: Extract<AlertRun, { status: "success" }>) => void;
}) => (
  <div className="divide-y divide-outline overflow-hidden rounded-2xl border border-outline bg-surface">
    {runs.map(run => (
      <RunRow key={run.id} run={run} meta={meta(run)} onAsk={onAsk} />
    ))}
  </div>
);

/**
 * Where the alert stopped being the alert the runs above it answered. Only a
 * changed question, scope or cadence puts it there — pausing an alert leaves
 * everything it ever answered still an answer to what it asks now.
 */
const EditedDivider = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3 text-xs text-text-dim">
    <span className="h-px flex-1 bg-outline" />
    {label}
    <span className="h-px flex-1 bg-outline" />
  </div>
);

/** The on-request run in flight (streaming its answer) or the one that failed. */
const RunProgressCard = ({ progress }: { progress: AlertRunProgress }) => {
  const { t } = useTranslation();

  switch (progress.status) {
    case "running":
      return (
        <article className="rounded-2xl border border-info/50 bg-surface p-4">
          <div className="mb-2 flex items-center gap-2 text-xs text-text-dim">
            <span className="h-2 w-2 animate-pulse rounded-full bg-info" />
            {progress.thinking !== null
              ? t(chatThinkingTranslationKey(progress.thinking))
              : t("watchlist.run_in_progress")}
          </div>
          {progress.text !== "" && (
            <div className="text-sm text-text">
              <ChatMarkdown content={progress.text} />
            </div>
          )}
        </article>
      );
    case "failed":
      return (
        <div className="rounded-2xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger-strong">
          {t("watchlist.run_failed")}:{" "}
          {progress.publicMessage
            ? formatPublicMessage(progress.publicMessage, t)
            : progress.message}
        </div>
      );
  }
};

/**
 * The alert's own prompt, scope and cadence, changed where the alert lives
 * rather than back in the composer that made it — that box belongs to whatever
 * is being asked next, and borrowing it made editing feel like starting over.
 */
const AlertEditor = ({
  alert,
  userOptions,
  processOptions,
  onDone,
}: {
  alert: Alert;
  userOptions: FilterOption[];
  processOptions: FilterOption[];
  onDone: () => void;
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(alert.title);
  const [prompt, setPrompt] = useState(alert.prompt);
  const [scope, setScope] = useState<WatchlistScope>(alert.scope);
  const [schedule, setSchedule] = useState<Schedule>(alert.schedule ?? { everyDays: WEEKLY_DAYS });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FormikErrors<AlertEditorFormValues>>({});

  // Opened by an explicit Edit, so the caret belongs in the prompt straight
  // away rather than one click later.
  const promptRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    promptRef.current?.focus();
  }, []);

  // The box is exactly as tall as what is in it. A fixed three rows left a
  // band of empty space under a one-line question, which read as a gap between
  // the question and whatever came next.
  useEffect(() => {
    const node = promptRef.current;
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${node.scrollHeight}px`;
  }, [prompt]);

  const store = (next: string, nextTitle: string) => {
    // A failure leaves the editor open with the edits still in it, which is the
    // only state from which they can be retried.
    setSaveError(null);
    setFieldErrors({});
    void update_alert(alert.id, { prompt: next, title: nextTitle, scope, schedule })
      .then(onDone)
      .catch(error => {
        const parsed = splitApiRequestFormErrors<AlertEditorFormValues>(
          error,
          t,
          ["title", "prompt", "scope.window.days", "schedule.everyDays"],
          {
            "scope.window_days": "scope.window.days",
            "schedule.every_days": "schedule.everyDays",
          },
        );
        setFieldErrors(parsed.fieldErrors);
        setSaveError(parsed.remainder);
        setSaving(false);
      });
  };

  const save = () => {
    const next = prompt.trim();
    if (next === "" || scope.userIds.length === 0 || !hasProcessSelection(scope) || saving) return;
    setSaving(true);
    store(next, title.trim());
  };

  return (
    <div className="rounded-2xl border border-outline bg-surface p-4 shadow-sm">
      <input
        value={title}
        onChange={event => {
          setTitle(event.target.value);
          setFieldErrors(errors => ({ ...errors, title: undefined }));
        }}
        placeholder={t("watchlist.title_placeholder")}
        className={clsx(
          "w-full bg-transparent text-lg font-medium text-text outline-none placeholder:text-text-dim",
          getIn(fieldErrors, "title") && "rounded border border-danger/60 px-1",
        )}
      />
      {getIn(fieldErrors, "title") && (
        <p className="mt-1 text-xs text-danger-strong">{getIn(fieldErrors, "title")}</p>
      )}
      <textarea
        ref={promptRef}
        rows={1}
        value={prompt}
        onChange={event => {
          setPrompt(event.target.value);
          setFieldErrors(errors => ({ ...errors, prompt: undefined }));
        }}
        className={clsx(
          "mt-1 w-full resize-none overflow-hidden bg-transparent text-sm text-text-mid outline-none placeholder:text-text-dim",
          getIn(fieldErrors, "prompt") && "rounded border border-danger/60 px-1",
        )}
      />
      {getIn(fieldErrors, "prompt") && (
        <p className="mt-1 text-xs text-danger-strong">{getIn(fieldErrors, "prompt")}</p>
      )}

      {/* The pickers below collapse a selection to a count, so the names are
          spelled out above them — the same chips the alert is read by, still
          removable one at a time while it is being edited. */}
      <ScopeChips
        scope={scope}
        userOptions={userOptions}
        processOptions={processOptions}
        onChange={setScope}
        className="mt-3"
      />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <FilterMultiSelect
          pill
          icon={<User size={12} className="shrink-0 text-text-dim" />}
          allLabel={t("watchlist.all_people")}
          searchPlaceholder={t("search_people")}
          multipleLabel={count => t("dashboard.people_selected", { count })}
          selectAllLabel={t("watchlist.all_people")}
          emptyLabel={t("watchlist.select_people")}
          value={scope.userIds}
          options={userOptions}
          onChange={userIds => setScope({ ...scope, userIds })}
        />

        <FilterMultiSelect
          pill
          icon={<Route size={12} className="shrink-0 text-text-dim" />}
          countLabel={t("process")}
          value={
            scope.processSlugs.length === 0 && scope.allProcesses !== false
              ? processOptions.map(option => option.id)
              : scope.processSlugs
          }
          options={processOptions}
          onChange={processSlugs => setScope({ ...scope, processSlugs, allProcesses: false })}
          allLabel={t("watchlist.all_processes")}
          emptyLabel={t("watchlist.select_processes")}
          selectAllLabel={t("watchlist.all_processes")}
          searchPlaceholder={t("search_in_processes")}
        />

        <DaysSelect
          value={scope.window.days}
          onChange={days => {
            setScope({ ...scope, window: { days } });
            setFieldErrors(errors => ({ ...errors, scope: undefined }));
          }}
          presets={WINDOW_PRESETS}
          format={days => t("watchlist.window_last_n_days", { count: days })}
          unitLabel={t("watchlist.days_back")}
        />
        {getIn(fieldErrors, "scope.window.days") && (
          <p className="w-full text-xs text-danger-strong">
            {getIn(fieldErrors, "scope.window.days")}
          </p>
        )}

        <DaysSelect
          value={schedule.everyDays}
          onChange={everyDays => {
            setSchedule({ everyDays });
            setFieldErrors(errors => ({ ...errors, schedule: undefined }));
          }}
          presets={SCHEDULE_PRESETS}
          format={days =>
            days === WEEKLY_DAYS
              ? t("watchlist.runs_weekly")
              : t("watchlist.runs_every_n_days_count", { count: days })
          }
          unitLabel={t("watchlist.days_between_runs")}
        />
        {getIn(fieldErrors, "schedule.everyDays") && (
          <p className="w-full text-xs text-danger-strong">
            {getIn(fieldErrors, "schedule.everyDays")}
          </p>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onDone}
            className="rounded-full px-3 py-1.5 text-xs text-text-mid transition hover:bg-muted-surface hover:text-text"
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={
              prompt.trim() === "" ||
              scope.userIds.length === 0 ||
              !hasProcessSelection(scope) ||
              saving
            }
            className="rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-white transition hover:opacity-90 disabled:opacity-40"
          >
            {t("save")}
          </button>
        </div>
      </div>
      {saveError && <p className="mt-3 text-xs text-danger-strong">{saveError}</p>}
    </div>
  );
};

/**
 * One alert on its own screen: every notification it has ever sent, oldest
 * first, with a chat about the newest one pinned to the bottom.
 */
const AlertDetail = ({
  alert,
  onBack,
  handlers,
  userOptions,
  processOptions,
  startInEdit = false,
}: AlertDetailProps) => {
  const { t } = useTranslation();
  const { relativeTime, dateTime, cadence } = useAlertLabels();
  const [editing, setEditing] = useState(startInEdit);
  const ask = useAskSidebar();
  const askAboutResult = (run: Extract<AlertRun, { status: "success" }>) => {
    const label = `${alert.title || t("chat_alert_result")} · ${dateTime(run.ranAt)}`;
    ask.openQuestion({
      context: alertResultContext(alert, run, userOptions, processOptions, label),
    });
  };
  // The list's Edit navigates here; from inside, it is already here, so the
  // same action just opens the editor in place.
  const actions = useAlertActions(alert, { ...handlers, onEdit: () => setEditing(true) });
  const progress = useRunProgress(alert.id);
  const latest = alert.runs[0] ?? null;
  const running = progress?.status === "running";
  const timing = [
    cadence(alert.schedule),
    !alert.enabled && t("paused"),
    latest && relativeTime(latest.ranAt),
  ]
    .filter(Boolean)
    .join(" · ");

  // `runs` is newest first; reading the column top to bottom should walk
  // forwards in time, so the past ones are reversed and the newest lands last.
  const pastRuns = useMemo(() => alert.runs.slice(1).reverse(), [alert.runs]);

  // Where the edit divider goes, as an index into the whole column of runs
  // (the past ones, then the latest): before the first run that answered the
  // current question, or past the end when no run has yet. `-1` is an alert
  // nobody has edited, which needs no divider at all.
  const dividerAt = useMemo(() => {
    const editedAt = Date.parse(alert.editedAt);
    if (!(editedAt > Date.parse(alert.createdAt))) return -1;
    const column = latest ? [...pastRuns, latest] : [];
    const at = column.findIndex(run => Date.parse(run.ranAt) > editedAt);
    return at === -1 ? column.length : at;
  }, [alert.editedAt, alert.createdAt, pastRuns, latest]);
  const editedLabel = t("watchlist.alert_edited_at", { at: dateTime(alert.editedAt) });
  const beforeEdit = dividerAt === -1 ? pastRuns : pastRuns.slice(0, dividerAt);
  const afterEdit = dividerAt === -1 ? [] : pastRuns.slice(dividerAt);

  // Reading opens on the newest notification; editing opens on the form at the top.
  const scrollRevision = `${alert.runs.length}:${progress?.status === "running" ? progress.text.length : 0}`;
  const { scrollRef, detached, scrollProps, scrollToBottom } = useChatAutoScroll(
    scrollRevision,
    !editing,
    alert.id,
  );
  useEffect(() => {
    const node = scrollRef.current;
    if (node && editing) node.scrollTop = 0;
  }, [editing, scrollRef]);

  return (
    // h-full rather than h-screen: this fills the app's scroll container, which
    // is already a title bar short of the viewport. See the same note on the
    // watchlist screen's root.
    <div className="app-scroll-fill flex h-full flex-col overflow-hidden bg-background">
      <header className="flex shrink-0 items-center gap-2 border-b border-outline px-4 py-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-text-mid transition hover:bg-muted-surface hover:text-text"
        >
          <ArrowLeft size={18} />
          {t("watchlist.back_to_list")}
        </button>

        {/* The same actions the row's overflow menu offers, so an alert can be
            paused or dropped without going back out to the list. */}
        {/* Icon-only buttons: the label is the only thing naming them, so the
            tooltip opens on hover with no delay rather than waiting like the
            browser's own `title` does. */}
        <Tooltip.Provider delayDuration={0}>
          <div className="ml-auto flex items-center gap-1">
            {actions.map(({ key, Icon, label, run, danger }) => (
              <Tooltip.Root key={key}>
                <Tooltip.Trigger asChild>
                  <button
                    type="button"
                    onClick={run}
                    aria-label={label}
                    className={clsx(
                      "rounded-lg p-2 text-text-dim transition hover:bg-muted-surface",
                      danger ? "hover:text-danger" : "hover:text-text",
                    )}
                  >
                    <Icon size={18} />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    side="bottom"
                    sideOffset={6}
                    className="z-50 select-none rounded-md border border-outline bg-surface px-2 py-1 text-xs font-medium text-text shadow-md"
                  >
                    {label}
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            ))}
          </div>
        </Tooltip.Provider>
      </header>

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div ref={scrollRef} {...scrollProps} className="flex-1 overflow-y-auto">
          <div className="mx-auto flex max-w-3xl flex-col gap-6 px-6 py-8">
            {editing ? (
              <AlertEditor
                alert={alert}
                userOptions={userOptions}
                processOptions={processOptions}
                onDone={() => setEditing(false)}
              />
            ) : (
              <div>
                {/* The name leads and the question follows it, so the page
                    opens with what this alert is before what it asks. An alert
                    with no name is its question, and repeating it below would
                    be printing the same line twice. */}
                <h1 className="text-3xl font-semibold text-text">{alert.title || alert.prompt}</h1>
                {alert.title && <p className="mt-2 text-sm text-text-mid">{alert.prompt}</p>}
                {timing && (
                  <p className="mt-3 flex flex-wrap items-center gap-1.5 text-sm text-text-dim">
                    <Clock size={14} />
                    {timing}
                  </p>
                )}
                {/* What the alert is actually evaluated over. Without it the
                    page states a question and a cadence and leaves the scope —
                    the half of an alert nobody can infer from its answers — to
                    be guessed at, or found by opening the editor. */}
                <ScopeSummary
                  scope={alert.scope}
                  userOptions={userOptions}
                  processOptions={processOptions}
                  className="mt-3"
                />
              </div>
            )}

            {latest === null ? (
              // The in-flight first run replaces the "never run" placeholder.
              progress === null && (
                <p className="text-sm text-text-dim">{t("watchlist.never_run")}</p>
              )
            ) : (
              <>
                {/* Split around the edit: the runs that answered the old
                    question, the divider saying so, then the rest. */}
                {beforeEdit.length > 0 && (
                  <PastRunList
                    onAsk={askAboutResult}
                    runs={beforeEdit}
                    meta={run => dateTime(run.ranAt)}
                  />
                )}

                {dividerAt >= 0 && dividerAt <= pastRuns.length && (
                  <EditedDivider label={editedLabel} />
                )}

                {afterEdit.length > 0 && (
                  <PastRunList
                    onAsk={askAboutResult}
                    runs={afterEdit}
                    meta={run => dateTime(run.ranAt)}
                  />
                )}

                <section className="flex flex-col gap-3">
                  <h2 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-text-dim">
                    <Bell size={14} />
                    {t("watchlist.latest_notification")}
                  </h2>
                  <div className="overflow-hidden rounded-2xl border border-outline bg-surface">
                    <RunRow
                      onAsk={askAboutResult}
                      key={latest.id}
                      run={latest}
                      meta={dateTime(latest.ranAt)}
                      defaultOpen
                    />
                  </div>
                </section>

                {/* Edited since the last run: nothing below the divider yet. */}
                {dividerAt > pastRuns.length && <EditedDivider label={editedLabel} />}
              </>
            )}

            {progress && <RunProgressCard progress={progress} />}
          </div>
        </div>
        {running && detached && !editing && (
          <DetachedStreamingIndicator onClick={scrollToBottom} label={t("loading_ellipsis")} />
        )}
      </div>
    </div>
  );
};

export default AlertDetail;
